import { useState } from 'react';
import { Link } from 'react-router-dom';
import { redirectToApp } from '../../config/domains';
import {
  Users,
  DollarSign,
  CheckCircle,
  ArrowRight,
  Star,
  MessageCircle,
  Navigation,
  Gamepad2,
  Hotel,
  Trophy,
  Phone,
  Utensils,
  Menu,
  X,
  Mail
} from 'lucide-react';
import { Toaster } from 'react-hot-toast';
import PublicContactForm from './PublicContactForm';

const MarketingHomepage = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Custom login handler that works correctly in all environments
  const handleLogin = (e) => {
    e.preventDefault();
    const hostname = window.location.hostname;

    // For preview/staging environments, remove the ?view=marketing parameter and show app
    if (hostname === 'preview.teamrezy.com' ||
        hostname === 'staging.teamrezy.com' ||
        hostname.includes('preview.') ||
        hostname.includes('staging.')) {
      // Simply remove the view parameter to show the app
      window.location.href = window.location.origin;
    } else {
      // For production, redirect to app subdomain
      redirectToApp('/');
    }
  };

  // Simple, reliable scroll function with iOS fix
  const scrollToSection = (sectionId) => {
    // Close mobile menu immediately
    setMobileMenuOpen(false);
    
    // Use a shorter delay for better responsiveness
    setTimeout(() => {
      const element = document.getElementById(sectionId);
      if (element) {
        // Standard navigation offset (80px for sticky nav)
        let offset = 80;
        
        // iOS mobile portrait mode needs extra offset
        const isMobile = window.innerWidth < 768;
        const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
        
        if (isMobile && isIOS) {
          // More generous offset for iOS mobile portrait mode
          offset = 100;
        }
        
        // Get element position and calculate target scroll position
        const elementPosition = element.getBoundingClientRect().top + window.scrollY;
        const targetPosition = elementPosition - offset;
        
        // Scroll to calculated position
        window.scrollTo({
          top: targetPosition,
          behavior: 'smooth'
        });
      }
    }, 150);
  };
  const features = [
    {
      icon: <Trophy className="h-6 w-6" />,
      title: "Tournament-Ready Platform",
      description: "We partner with tournament directors to pre-load schedules, teams, and game locations. View your schedule as soon as it's released - before you leave home."
    },
    {
      icon: <Navigation className="h-6 w-6" />,
      title: "Smart Location Mapping",
      description: "Search venues by distance from your tournament hotel or game locations. See exact drive times and never book in the wrong direction."
    },
    {
      icon: <Utensils className="h-6 w-6" />,
      title: "Partnered Venues",
      description: "Browse restaurants and entertainment venues we've vetted and partnered with. They understand large groups and youth sports teams."
    },
    {
      icon: <DollarSign className="h-6 w-6" />,
      title: "Zero Cost to Teams",
      description: "No fees, no subscriptions. Just pay your deposit which comes off the final bill at the venue."
    }
  ];

  const benefits = [
    "Venues that can split bills by family - even when not sitting together",
    "Order By Jersey Number system keeps family checks organized",
    "Kid-friendly restaurants with entertainment (arcades, pool tables, play areas)",
    "Google Maps integration with drive times from hotel and game venues",
    "Works for tournaments OR standalone games - flexible for any trip",
    "Direct venue communication - venues can accept, decline, or propose alternative times",
    "Geo-coded venues for precise distance calculations",
    "Fast responses from partnered venues"
  ];

  const testimonials = [
    {
      name: "Sarah Johnson",
      role: "Hockey Team Manager",
      content: "Tournament schedule was already loaded when we registered. Found a great restaurant near our hotel that accepted our reservation within hours. So easy!",
      rating: 5
    },
    {
      name: "Mike Chen",
      role: "Travel Basketball Parent Coordinator",
      content: "The distance calculations from our hotel saved us from booking a restaurant 30 minutes in the wrong direction. All the partnered venues were perfect for our group.",
      rating: 5
    },
    {
      name: "Lisa Thompson",
      role: "Tournament Team Mom",
      content: "Love that venues can respond directly! Got confirmation within hours. The split billing by jersey number is a game-changer for our families.",
      rating: 5
    }
  ];

  const processSteps = [
    {
      step: "1",
      title: "Find Your Tournament",
      description: "Your tournament is already loaded. Just search and select - no data entry needed."
    },
    {
      step: "2",
      title: "Set Your Hotel Location",
      description: "Mark where you're staying to see drive times and avoid wrong-way bookings."
    },
    {
      step: "3",
      title: "Browse Partnered Venues",
      description: "View kid-friendly restaurants near your hotel and games. See amenities, distance, and availability."
    },
    {
      step: "4",
      title: "Submit Your Request",
      description: "Venues receive your request via email and can accept, decline, or propose alternative times directly."
    }
  ];

  return (
    <div className="min-h-screen bg-white" id="top">
      {/* Navigation */}
      <nav className="bg-white shadow-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <div className="flex items-center">
              <div
                onClick={() => {
                  // Clear the hash from URL
                  window.history.replaceState(null, '', window.location.pathname);
                  // Multiple methods to ensure scroll works
                  window.scrollTo(0, 0);
                  document.getElementById('top').scrollIntoView();
                  // Force a small delay to ensure scroll happens
                  setTimeout(() => {
                    window.scrollTo(0, 0);
                  }, 10);
                }}
                style={{ cursor: 'pointer' }}
              >
                <img
                  src="/TeamRezyLogo.png"
                  alt="TeamRezy Logo"
                  className="h-20 w-auto object-contain hover:opacity-80 transition-opacity duration-200"
                />
              </div>
            </div>
            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center space-x-8">
              <a href="#how-it-works" className="text-gray-700 hover:text-navy-600 transition-colors">How It Works</a>
              <a href="#features" className="text-gray-700 hover:text-navy-600 transition-colors">Features</a>
              <a href="#benefits" className="text-gray-700 hover:text-navy-600 transition-colors">Benefits</a>
              <a href="#testimonials" className="text-gray-700 hover:text-navy-600 transition-colors">Testimonials</a>
              <a href="#contact" className="text-gray-700 hover:text-navy-600 transition-colors">Contact</a>
              <a
                href="#"
                onClick={handleLogin}
                className="bg-navy-600 text-white px-4 py-2 rounded-lg hover:bg-navy-700 transition-colors"
              >
                Login
              </a>
            </div>

            {/* Mobile Navigation */}
            <div className="md:hidden flex items-center space-x-4">
              <a
                href="#"
                onClick={handleLogin}
                className="bg-navy-600 text-white px-3 py-2 rounded-lg hover:bg-navy-700 transition-colors text-sm"
              >
                Login
              </a>
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="text-gray-700 hover:text-navy-600 transition-colors p-2"
                aria-label="Toggle menu"
              >
                {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu Panel */}
        <div className={`md:hidden bg-white border-t border-gray-200 shadow-lg transition-all duration-300 ease-in-out ${
          mobileMenuOpen
            ? 'opacity-100 max-h-96 visible'
            : 'opacity-0 max-h-0 invisible overflow-hidden'
        }`}>
          <div className="px-4 py-4 space-y-1">
            <a
              href="#how-it-works"
              className="block text-gray-700 hover:text-navy-600 hover:bg-gray-50 transition-all duration-200 py-3 px-2 rounded-lg text-lg font-medium"
              onClick={(e) => {
                e.preventDefault();
                scrollToSection('how-it-works');
                // Immediate fallback if custom scroll fails
                setTimeout(() => {
                  const element = document.getElementById('how-it-works');
                  if (element) {
                    element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  }
                }, 500);
              }}
            >
              How It Works
            </a>
            <a
              href="#features"
              className="block text-gray-700 hover:text-navy-600 hover:bg-gray-50 transition-all duration-200 py-3 px-2 rounded-lg text-lg font-medium"
              onClick={(e) => {
                e.preventDefault();
                scrollToSection('features');
                // Immediate fallback if custom scroll fails
                setTimeout(() => {
                  const element = document.getElementById('features');
                  if (element) {
                    element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  }
                }, 500);
              }}
            >
              Features
            </a>
            <a
              href="#benefits"
              className="block text-gray-700 hover:text-navy-600 hover:bg-gray-50 transition-all duration-200 py-3 px-2 rounded-lg text-lg font-medium"
              onClick={(e) => {
                e.preventDefault();
                scrollToSection('benefits');
                // Immediate fallback if custom scroll fails
                setTimeout(() => {
                  const element = document.getElementById('benefits');
                  if (element) {
                    element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  }
                }, 500);
              }}
            >
              Benefits
            </a>
            <a
              href="#testimonials"
              className="block text-gray-700 hover:text-navy-600 hover:bg-gray-50 transition-all duration-200 py-3 px-2 rounded-lg text-lg font-medium"
              onClick={(e) => {
                e.preventDefault();
                scrollToSection('testimonials');
                // Immediate fallback if custom scroll fails
                setTimeout(() => {
                  const element = document.getElementById('testimonials');
                  if (element) {
                    element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  }
                }, 500);
              }}
            >
              Testimonials
            </a>
            <a
              href="#contact"
              className="block text-gray-700 hover:text-navy-600 hover:bg-gray-50 transition-all duration-200 py-3 px-2 rounded-lg text-lg font-medium"
              onClick={(e) => {
                e.preventDefault();
                scrollToSection('contact');
                // Immediate fallback if custom scroll fails
                setTimeout(() => {
                  const element = document.getElementById('contact');
                  if (element) {
                    element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  }
                }, 500);
              }}
            >
              Contact
            </a>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-navy-600 to-burgundy-700 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              Your Tournament Schedule Is Ready.<br/>Find Partnered Venues In Seconds.
            </h2>
            <p className="text-xl mb-8 text-blue-100 max-w-3xl mx-auto">
             We partner with tournament directors and local venues so everything is ready before you leave home. <br/>View your tournament schedule, search venues by distance to your hotel or game locations, and get fast confirmations.
            </p>
            <div className="flex justify-center space-x-4">
              <Link
                to="/register"
                className="bg-white text-navy-600 px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors inline-flex items-center"
              >
                Get Started Free
                <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
              <a
                href="#how-it-works"
                className="border-2 border-white text-white px-8 py-3 rounded-lg font-semibold hover:bg-white hover:text-navy-600 transition-colors"
              >
                See How It Works
              </a>
            </div>
            <p className="mt-6 text-sm text-blue-200">
              ✓ FREE for teams ✓ Tournament schedules pre-loaded ✓ Partnered venues
            </p>
          </div>
        </div>
      </section>

      {/* Value Proposition Bar */}
      <section className="bg-navy-800 text-white py-4">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-around items-center text-center">
            <div className="flex items-center space-x-2">
              <Trophy className="h-5 w-5 text-yellow-400" />
              <span className="text-sm">Pre-Loaded Tournaments</span>
            </div>
            <div className="flex items-center space-x-2">
              <Users className="h-5 w-5 text-yellow-400" />
              <span className="text-sm">Partnered Venues</span>
            </div>
            <div className="flex items-center space-x-2">
              <CheckCircle className="h-5 w-5 text-yellow-400" />
              <span className="text-sm">Fast Venue Responses</span>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section id="how-it-works" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h3 className="text-3xl font-bold text-gray-900 mb-4">
              Four Simple Steps to Confirmed Reservations
            </h3>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Tournament data is pre-loaded by directors. Venues respond directly to your requests.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {processSteps.map((item, index) => (
              <div key={index} className="relative">
                <div className="bg-white rounded-xl p-6 shadow-md hover:shadow-lg transition-shadow">
                  <div className="bg-navy-600 text-white w-12 h-12 rounded-full flex items-center justify-center font-bold text-xl mb-4">
                    {item.step}
                  </div>
                  <h4 className="text-xl font-semibold text-gray-900 mb-2">{item.title}</h4>
                  <p className="text-gray-600">{item.description}</p>
                </div>
                {index < processSteps.length - 1 && (
                  <ArrowRight className="hidden lg:block absolute top-1/2 -right-4 transform -translate-y-1/2 text-gray-400 h-6 w-6" />
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h3 className="text-3xl font-bold text-gray-900 mb-4">
              Built For Tournament Directors and Team Managers
            </h3>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              We work with tournament directors to load schedules and partner with local venues. Access your tournament schedule and search nearby restaurants before you even leave home.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <div key={index} className="bg-white rounded-xl p-6 shadow-md hover:shadow-lg transition-shadow">
                <div className="text-navy-600 mb-4">{feature.icon}</div>
                <h4 className="text-xl font-semibold text-gray-900 mb-2">{feature.title}</h4>
                <p className="text-gray-600">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section id="benefits" className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h3 className="text-3xl font-bold text-gray-900 mb-6">
                Partnered Venues That Understand Youth Sports Teams
              </h3>
              <p className="text-lg text-gray-600 mb-6">
                We partner with venues that meet our large group standards and can respond quickly to reservation requests. Browse options, submit requests, and get fast confirmations.
              </p>
              <div className="space-y-4">
                {benefits.map((benefit, index) => (
                  <div key={index} className="flex items-start">
                    <CheckCircle className="h-6 w-6 text-green-500 mr-3 flex-shrink-0 mt-0.5" />
                    <p className="text-gray-700">{benefit}</p>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-gradient-to-br from-navy-600 to-burgundy-700 rounded-2xl p-8 text-white">
              <h4 className="text-2xl font-bold mb-6">What Makes Us Different</h4>
              <div className="space-y-6">
                <div className="flex items-start">
                  <Hotel className="h-8 w-8 mr-4 flex-shrink-0 text-yellow-400" />
                  <div>
                    <h5 className="font-semibold mb-1">Hotel-Based Distance Calculations</h5>
                    <p className="text-blue-100">Know exactly how far each venue is from where you're staying AND your games</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <Utensils className="h-8 w-8 mr-4 flex-shrink-0 text-yellow-400" />
                  <div>
                    <h5 className="font-semibold mb-1">Pre-Verified Large Group Venues</h5>
                    <p className="text-blue-100">Every venue can handle 20-100+ people with split billing capabilities</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <Gamepad2 className="h-8 w-8 mr-4 flex-shrink-0 text-yellow-400" />
                  <div>
                    <h5 className="font-semibold mb-1">Kid Entertainment Info</h5>
                    <p className="text-blue-100">Know which restaurants have arcades, play areas, and activities for kids</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <MessageCircle className="h-8 w-8 mr-4 flex-shrink-0 text-yellow-400" />
                  <div>
                    <h5 className="font-semibold mb-1">Direct Venue Communication</h5>
                    <p className="text-blue-100">Venues receive email notifications and can accept, decline, or propose new times quickly</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section id="testimonials" className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h3 className="text-3xl font-bold text-gray-900 mb-4">
              Team Managers Love The Simplicity
            </h3>
            <p className="text-lg text-gray-600">
              Join hundreds of teams who've simplified their tournament logistics
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="bg-white rounded-xl p-6 shadow-md">
                <div className="flex mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} className="h-5 w-5 text-yellow-400 fill-current" />
                  ))}
                </div>
                <p className="text-gray-700 mb-4 italic">"{testimonial.content}"</p>
                <div>
                  <p className="font-semibold text-gray-900">{testimonial.name}</p>
                  <p className="text-sm text-gray-600">{testimonial.role}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* No Cost Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gradient-to-r from-green-50 to-blue-50 rounded-3xl p-12 text-center">
            <DollarSign className="h-16 w-16 text-green-600 mx-auto mb-6" />
            <h3 className="text-3xl font-bold text-gray-900 mb-4">
              Completely FREE for Teams
            </h3>
            <p className="text-xl text-gray-700 mb-8 max-w-3xl mx-auto">
              No subscription fees. No booking fees. No hidden costs.
              <br/>Your deposit simply comes off your final bill at the venue.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl mx-auto">
              <div className="bg-white rounded-xl p-6 shadow-md">
                <CheckCircle className="h-8 w-8 text-green-500 mx-auto mb-3" />
                <h4 className="font-semibold text-gray-900 mb-2">No Setup Fees</h4>
                <p className="text-gray-600">Start using TeamRezy immediately at no cost</p>
              </div>
              <div className="bg-white rounded-xl p-6 shadow-md">
                <CheckCircle className="h-8 w-8 text-green-500 mx-auto mb-3" />
                <h4 className="font-semibold text-gray-900 mb-2">Deposits Apply to Bill</h4>
                <p className="text-gray-600">Your deposit isn't extra - it comes off your venue bill</p>
              </div>
              <div className="bg-white rounded-xl p-6 shadow-md">
                <CheckCircle className="h-8 w-8 text-green-500 mx-auto mb-3" />
                <h4 className="font-semibold text-gray-900 mb-2">Save Hours of Searching</h4>
                <p className="text-gray-600">No more calling around. Browse only venues that welcome large youth sports groups</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-navy-600 to-burgundy-700 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h3 className="text-3xl md:text-4xl font-bold mb-4">
            Your Next Tournament Just Got 10x Easier
          </h3>
          <p className="text-xl mb-8 text-blue-100">
            Tournament schedules pre-loaded. Partnered venues ready. Fast reservation responses.
          </p>
          <div className="flex flex-col sm:flex-row justify-center space-y-4 sm:space-y-0 sm:space-x-4">
            <Link
              to="/register"
              className="bg-white text-navy-600 px-8 py-4 rounded-lg font-semibold hover:bg-gray-100 transition-colors inline-flex items-center justify-center"
            >
              Start Free - Takes 2 Minutes
              <ArrowRight className="ml-2 h-5 w-5" />
            </Link>
            <a
              href="tel:+1-615-701-6972"
              className="border-2 border-white text-white px-8 py-4 rounded-lg font-semibold hover:bg-white hover:text-navy-600 transition-colors inline-flex items-center justify-center"
            >
              <Phone className="mr-2 h-5 w-5" />
              Talk to Our Team
            </a>
          </div>
          <p className="mt-6 text-sm text-blue-200">
            Join 500+ teams who've simplified their tournament logistics
          </p>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h3 className="text-3xl font-bold text-gray-900 mb-4">
              Questions? We're Here to Help
            </h3>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Whether you're a team manager curious about our service or a venue interested in partnering with us, we'd love to hear from you.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            {/* Contact Form */}
            <PublicContactForm />

            {/* Contact Information */}
            <div className="space-y-6">
              <div className="bg-white rounded-lg shadow-md p-6">
                <h4 className="text-xl font-semibold mb-4">Get in Touch</h4>
                <div className="space-y-4">
                  <div className="flex items-start">
                    <Phone className="h-5 w-5 text-navy-600 mr-3 mt-1 flex-shrink-0" />
                    <div>
                      <p className="font-medium">Call Us</p>
                      <a href="tel:+1-615-701-6972" className="text-navy-600 hover:underline">615-701-6972</a>
                      <p className="text-sm text-gray-600">Monday - Friday, 9am - 6pm CT</p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <Mail className="h-5 w-5 text-navy-600 mr-3 mt-1 flex-shrink-0" />
                    <div>
                      <p className="font-medium">Email Us</p>
                      <a href="mailto:contactus@teamrezy.com" className="text-navy-600 hover:underline">contactus@teamrezy.com</a>
                      <p className="text-sm text-gray-600">We respond within 2-4 hours</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-br from-navy-600 to-burgundy-700 rounded-lg p-6 text-white">
                <h4 className="text-xl font-semibold mb-4">Quick Answers</h4>
                <div className="space-y-3">
                  <div>
                    <p className="font-medium text-yellow-200">For Teams:</p>
                    <p className="text-blue-100 text-sm">TeamRezy is completely free! We handle venue reservations for your tournaments at no cost to you.</p>
                  </div>
                  <div>
                    <p className="font-medium text-yellow-200">For Venues:</p>
                    <p className="text-blue-100 text-sm">Interested in partnering with us? We work with restaurants that can accommodate large groups (20-100+ people).</p>
                  </div>
                  <div>
                    <p className="font-medium text-yellow-200">Response Time:</p>
                    <p className="text-blue-100 text-sm">We typically respond to all inquiries within 2-4 hours during business days.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div>
              <h5 className="text-lg font-semibold mb-4">TeamRezy</h5>
              <p className="text-gray-400">
                Tournament-ready platform for youth sports teams.
                Pre-loaded schedules, partnered venues, fast responses.
              </p>
            </div>
            <div>
              <h5 className="text-lg font-semibold mb-4">For Teams</h5>
              <ul className="space-y-2 text-gray-400">
                <li><a href="#how-it-works" className="hover:text-white transition-colors">How It Works</a></li>
                <li><a href="#features" className="hover:text-white transition-colors">Features</a></li>
                <li><a href="#benefits" className="hover:text-white transition-colors">Benefits</a></li>
                <li><Link to="/register" className="hover:text-white transition-colors">Get Started</Link></li>
              </ul>
            </div>
            <div>
              <h5 className="text-lg font-semibold mb-4">For Venues</h5>
              <ul className="space-y-2 text-gray-400">
                <li><a href="/venue-partner" className="hover:text-white transition-colors">Become a Partner</a></li>
                <li><a href="/venue-benefits" className="hover:text-white transition-colors">Partner Benefits</a></li>
                <li><a href="/venue-requirements" className="hover:text-white transition-colors">Requirements</a></li>
                <li><a href="/venue-login" className="hover:text-white transition-colors">Venue Login</a></li>
              </ul>
            </div>
            <div>
              <h5 className="text-lg font-semibold mb-4">Support</h5>
              <ul className="space-y-2 text-gray-400">
                <li><a href="/faq" className="hover:text-white transition-colors">FAQ</a></li>
                <li><a href="/contact" className="hover:text-white transition-colors">Contact Us</a></li>
                <li><a href="tel:+1-615-701-6972" className="hover:text-white transition-colors">615-701-6972</a></li>
                <li><a href="#" onClick={handleLogin} className="hover:text-white transition-colors">Team Login</a></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 mt-8 pt-8 text-center text-gray-400">
            <p>&copy; 2024 TeamRezy. All rights reserved. | <a href="/privacy-policy.html" target="_blank" rel="noopener noreferrer" className="hover:text-white">Privacy</a> | <a href="/terms-of-service.html" target="_blank" rel="noopener noreferrer" className="hover:text-white">Terms</a></p>
          </div>
        </div>
      </footer>

      {/* Toast notifications */}
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 4000,
          style: {
            background: '#363636',
            color: '#fff',
          },
        }}
      />
    </div>
  );
};

export default MarketingHomepage;
